// Shared helpers for pages using ChoiceFilterPopover's draft/appliedFilters
// shape: { [fieldKey]: { op: "is"|"is_not", values: [...] } } for choice
// fields, or { type: "daterange", from, to } for date-range fields.
//
// Mirrors features/tasks/services/tasksViewDerivations.js's own
// hasActiveFilterDef/countActiveFilters/buildFilteredTasks (that page isn't
// migrated onto this shared version — out of scope for now, don't want to
// regression-test an already-working page's client-side filtering as a
// side effect) — this is the reusable version for pages newly adopting
// ChoiceFilterPopover (Epic Effort Report, and any future migration).

export function hasActiveFilterDef(filterDef) {
  if (!filterDef) return false;
  if (filterDef.type === "daterange") return !!(filterDef.from || filterDef.to);
  if (filterDef.type === "text") return !!filterDef.value?.trim();
  return Array.isArray(filterDef.values) && filterDef.values.length > 0;
}

export function countActiveFilters(appliedFilters) {
  return Object.values(appliedFilters || {}).filter(hasActiveFilterDef).length;
}

// Generic client-side filter over an already-fetched row array, supporting
// the same Is/Is-Not + All/Any semantics as Tasks' own buildFilteredTasks.
// `getFieldValue(row, fieldKey)` defaults to a plain `row[fieldKey]` lookup —
// pass one in when a field key doesn't map 1:1 to a row property name.
export function buildFilteredRows({ rows, appliedFilters, filterMode = "all", getFieldValue }) {
  const list = Array.isArray(rows) ? rows : [];
  const filterEntries = Object.entries(appliedFilters || {}).filter(([, def]) => hasActiveFilterDef(def));
  if (filterEntries.length === 0) return list;

  const getValue = getFieldValue || ((row, key) => row[key]);

  return list.filter((row) => {
    const checks = filterEntries.map(([field, filterDef]) => {
      if (filterDef.type === "daterange") {
        const raw = getValue(row, field);
        if (!raw) return false;
        const datePart = String(raw).slice(0, 10);
        if (filterDef.from && datePart < filterDef.from) return false;
        if (filterDef.to && datePart > filterDef.to) return false;
        return true;
      }
      if (filterDef.type === "text") {
        return String(getValue(row, field) || "").toLowerCase().includes((filterDef.value || "").toLowerCase());
      }
      const rowValue = getValue(row, field) ?? "";
      const match = (filterDef.values || []).includes(rowValue);
      return filterDef.op === "is_not" ? !match : match;
    });
    return filterMode === "all" ? checks.every(Boolean) : checks.some(Boolean);
  });
}
